// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import EngineeringIcon from '@mui/icons-material/Engineering'
import SavingsIcon from '@mui/icons-material/Savings'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import SignpostIcon from '@mui/icons-material/Signpost'
import GppGoodIcon from '@mui/icons-material/GppGood'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

/** Tint of the brand green, for the icon discs and the closing banner. */
const GREEN_DISC = 'rgba(45, 206, 137, 0.10)'
const GREEN_BANNER = 'rgba(45, 206, 137, 0.08)'

type Item = {
  icon: ReactElement
  title: string
  description: string
}

export default function WhyUsSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const mobileMode = w < breakpoints.mobileLarge

  const iconSx = {
    fontSize: '2.1rem',
    color: themeColors.kroemtech.dunkelblau
  }

  const items: Item[] = [
    {
      icon: <PrecisionManufacturingIcon sx={iconSx} />,
      title: t('whyUs.items.01.title'),
      description: t('whyUs.items.01.description')
    },
    {
      icon: <FactCheckIcon sx={iconSx} />,
      title: t('whyUs.items.02.title'),
      description: t('whyUs.items.02.description')
    },
    {
      icon: <EngineeringIcon sx={iconSx} />,
      title: t('whyUs.items.03.title'),
      description: t('whyUs.items.03.description')
    },
    {
      icon: <SavingsIcon sx={iconSx} />,
      title: t('whyUs.items.04.title'),
      description: t('whyUs.items.04.description')
    },
    {
      icon: <VerifiedUserIcon sx={iconSx} />,
      title: t('whyUs.items.05.title'),
      description: t('whyUs.items.05.description')
    },
    {
      icon: <SignpostIcon sx={iconSx} />,
      title: t('whyUs.items.06.title'),
      description: t('whyUs.items.06.description')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileLarge) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getCard = (item: Item) => (
    <Box
      key={item.title}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1.25rem',
        height: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: '4rem',
          height: '4rem',
          borderRadius: '50%',
          background: GREEN_DISC,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {item.icon}
      </Box>

      <Box>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1.05rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau
          }}
        >
          {item.title}
        </Typography>

        <Box
          sx={{
            width: '1.75rem',
            borderTop: `2px solid ${themeColors.success.main}`,
            margin: '0.9rem 0'
          }}
        />

        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.65,
            color: themeColors.gray.defaultText
          }}
        >
          {item.description}
        </Typography>
      </Box>
    </Box>
  )

  const getFooter = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: mobileMode ? 'flex-start' : 'center',
        gap: '1.25rem',
        background: GREEN_BANNER,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.5rem 1.75rem',
        marginTop: '2rem'
      }}
    >
      <GppGoodIcon
        sx={{ fontSize: '2.25rem', color: themeColors.success.darker }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: getFontBody(),
            fontWeight: 700,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.25rem'
          }}
        >
          {t('whyUs.footer.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {t('whyUs.footer.text')}
        </Typography>
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: themeColors.success.main,
          marginBottom: '0.5rem'
        }}
      >
        {t('whyUs.eyebrow')}
      </Typography>
      <Box
        sx={{
          width: '2.5rem',
          borderTop: `3px solid ${themeColors.success.main}`,
          marginBottom: '1.5rem'
        }}
      />

      <Typography
        variant="h2"
        component="h2"
        fontSize={getFontH2()}
        sx={{
          fontWeight: 700,
          lineHeight: 1.15,
          color: themeColors.kroemtech.dunkelblau,
          maxWidth: '28rem',
          marginBottom: '1.25rem'
        }}
      >
        {t('whyUs.title')}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '42rem',
          marginBottom: '2.5rem'
        }}
      >
        {t('whyUs.description')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {items.map(item => getCard(item))}
      </Box>

      {getFooter()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
